import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import { Briefcase, Users, Eye, TrendingUp, Plus } from 'lucide-react';
import { Navigation } from '../../components/Navigation';

export function EmployerDashboard() {
  const { profile } = useAuthStore();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    totalViews: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      console.log('[EmployerDashboard] Loading stats for employer:', profile.id);
      loadStats();
    } else {
      console.warn('[EmployerDashboard] Profile not available yet');
      setLoading(false);
    }
  }, [profile]);

  const loadStats = async () => {
    if (!profile?.id) {
      console.warn('[EmployerDashboard] Cannot load stats without profile');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: jobs } = await supabase
        .from('jobs')
        .select('id, status, views_count')
        .eq('employer_id', profile.id);

      const totalJobs = jobs?.length || 0;
      const activeJobs = jobs?.filter(j => j.status === 'active').length || 0;
      const totalViews = jobs?.reduce((sum, j) => sum + j.views_count, 0) || 0;

      const jobIds = jobs?.map(j => j.id) || [];

      let totalApplications = 0;
      if (jobIds.length > 0) {
        const { count } = await supabase
          .from('applications')
          .select('*', { count: 'exact', head: true })
          .in('job_id', jobIds);

        totalApplications = count || 0;
      }

      setStats({
        totalJobs,
        activeJobs,
        totalApplications,
        totalViews,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Jobs',
      value: stats.totalJobs,
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Active Jobs',
      value: stats.activeJobs,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
    },
    {
      label: 'Applications',
      value: stats.totalApplications,
      icon: Users,
      color: 'from-orange-500 to-amber-500',
    },
    {
      label: 'Total Views',
      value: stats.totalViews,
      icon: Eye,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Employer Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your job postings and applications</p>
          </div>
          <Link
            to="/employer/jobs/new"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition"
          >
            <Plus className="w-5 h-5" />
            Post New Job
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-lg p-6">
              <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${stat.color} mb-4`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link
                to="/employer/jobs"
                className="block p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition"
              >
                <h3 className="font-semibold text-gray-900">Manage Jobs</h3>
                <p className="text-sm text-gray-600 mt-1">View and edit your job postings</p>
              </Link>
              <Link
                to="/employer/applications"
                className="block p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition"
              >
                <h3 className="font-semibold text-gray-900">Review Applications</h3>
                <p className="text-sm text-gray-600 mt-1">Check new candidate applications</p>
              </Link>
              <Link
                to="/employer/messages"
                className="block p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition"
              >
                <h3 className="font-semibold text-gray-900">Messages</h3>
                <p className="text-sm text-gray-600 mt-1">Chat with applicants</p>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Getting Started</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Post your first job</h3>
                  <p className="text-sm text-gray-600 mt-1">Create a detailed job posting to attract candidates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Review applications</h3>
                  <p className="text-sm text-gray-600 mt-1">AI-powered matching helps you find the best candidates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Connect with talent</h3>
                  <p className="text-sm text-gray-600 mt-1">Message candidates directly through the platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
